import type { CSSProperties } from 'react';

/**
 * How wide a product's gallery frame is, and what shape it is.
 *
 * WHY THIS IS A LIB. The gallery is rendered in THREE places that share no
 * code: the real product page and the two hand-rolled page previews inside the
 * create and edit forms. A ratio, a default or a cap that lived in one of them
 * would be wrong in the other two within a release, and the seller's preview
 * disagreeing with the published page is the specific failure this whole file
 * exists to prevent.
 *
 * WHY IT LIVES ON `details` AND NOT ON `design`. `product.design` is gated
 * behind the "Customise this product page" master switch, whose other settings
 * are theme overrides (starfield, accent, light/dark). Gallery width is
 * structural, not a theme, and a seller should not have to turn on a dark-mode
 * background effect to change it. `details.coverInGallery` is already the home
 * for gallery display settings, so these sit beside it.
 */

/**
 * `standard` is the DEFAULT and the two-column layout: a square frame in the
 * left column with the name, price and buy button beside it.
 * `full_width` spans the page's content area with the seller's chosen ratio and
 * a thumbnail strip beneath, in the shape of a Gumroad product gallery.
 * `none` renders no gallery at all and keeps the rest of the full-width page
 * shape, so the page opens on the name and prose with the buy box beside them.
 *
 * WHY `none` HAS TO EXIST. Without it a seller cannot get a page with no image
 * on it, however hard they try: with an empty gallery and the cover excluded,
 * the product page falls back to showing the cover in the frame anyway (the
 * cover is mandatory, because the marketplace cards and the og:image need it).
 * So a text or code product whose only image is a logo had no way to avoid a
 * letterboxed logo sitting where the buyer looks first.
 */
export type FrameWidthMode = 'standard' | 'full_width' | 'none';

/**
 * The frame every product gets until its seller says otherwise.
 *
 * `standard`: the two-column layout, with a square frame beside the name, price
 * and buy button. It was `full_width` and is not any more, which is a change to
 * how UNCONFIGURED products look, not only to what a new one starts on.
 *
 * The reasoning is that a default is what a seller gets when they have not
 * thought about it, and the two-column shape is the safer answer to that: the
 * price and the buy button are beside the image rather than below it, so they
 * are on screen when the page opens whatever the seller's picture happens to
 * be. Full width is the deliberate choice, made in the media step, by a seller
 * who has an image worth the whole width.
 *
 * It is BOTH the write-side start for a new product and the read-side fallback
 * for a document with no `frameWidthMode` on it, deliberately: two constants
 * would mean a product created last year and one created today disagree about
 * what "default" means, and the seller's editor would show one thing while the
 * published page showed the other. The cost is that a legacy product whose
 * seller never opened this setting changes shape. `resolveFrameWidthMode` is
 * what keeps that to products where NOBODY chose: an explicit
 * `frameWidthMode`, and the legacy `galleryPosition` under either of its
 * values, are read first and are unaffected.
 */
export const DEFAULT_FRAME_WIDTH_MODE: FrameWidthMode = 'standard';

/**
 * Frame ratios a seller can choose for the FULL-WIDTH mode.
 *
 * Only offered there, because it is the only mode with a ratio to choose: the
 * `standard` gallery is a column in a two-column grid and stays square so the
 * right-hand column keeps a predictable start. Images that are not square
 * letterbox inside the frame in both modes rather than being cropped.
 *
 * The list is closed rather than free-form. An arbitrary ratio is a way for a
 * seller to make their own page unusable (a 1:8 sliver, a 20:1 strip) on the
 * page carrying the price and the buy button, and none of the shapes people
 * actually shoot in are missing from this list.
 */
export const GALLERY_RATIOS: ReadonlyArray<{
  value: string;
  label: string;
  /**
   * Exempt from the height cap, so the frame is ALWAYS exactly the width of the
   * content area with no space either side.
   *
   * Only the widest shape carries this. The cap exists to stop a tall frame
   * pushing the price and buy button off screen, and at 21:9 that cannot happen
   * — a full-width frame is a little over a third of its own width in height,
   * so there is nothing for the cap to protect against and letting it bind only
   * produces the side gaps this option exists to remove.
   */
  fillsWidth?: true;
}> = [
  { value: '21/9', label: 'Page width (21:9)', fillsWidth: true },
  { value: '16/9', label: 'Widescreen (16:9)' },
  { value: '3/2', label: 'Photo (3:2)' },
  { value: '4/3', label: 'Classic (4:3)' },
  { value: '1/1', label: 'Square (1:1)' },
  { value: '4/5', label: 'Portrait (4:5)' },
];

export const DEFAULT_GALLERY_RATIO = '16/9';

/**
 * The shape a seller gets the MOMENT they pick the full-width frame.
 *
 * Page width, because that is what "full width" means when you read it on the
 * button: a frame that runs edge to edge of the content area with nothing
 * either side. 16:9 is the closest runner-up and it still leaves a gap on a
 * wide screen once the height cap binds, so a seller choosing the widest layout
 * had to make a second, non-obvious choice in a dropdown to actually get it.
 *
 * DELIBERATELY NOT `DEFAULT_GALLERY_RATIO`. That one is the READ-side fallback
 * for a stored document with no ratio on it, and moving it would silently
 * reshape every already-published page that never wrote the field. This one is
 * the WRITE-side default for a fresh choice in the form, so it only ever
 * applies to a seller who is looking at the preview while it happens.
 */
export const DEFAULT_FULL_WIDTH_RATIO = '21/9';

/**
 * Ceiling on the height of a full-width gallery, as a fraction of the viewport.
 *
 * Not cosmetic. Without it a 4:5 portrait frame at the content area's full
 * width (1368px) would be over 1700px tall, and the price, the tier selector
 * and the buy button would all sit below it — on the site's hottest page, for a
 * buyer who has just arrived. The cap is what keeps "full width" a layout
 * choice rather than a decision to hide the purchase controls.
 *
 * Set so the landscape ratios genuinely fill the width on an ordinary laptop
 * (16:9 at 1368px is 770px tall, well under this) and only the square and
 * portrait shapes are clamped — where filling the width would mean a frame
 * taller than the screen. When it does bind, the frame keeps its ratio and gets
 * NARROWER than the container, and the spare width falls either side of a
 * centred frame.
 */
export const GALLERY_MAX_VH = 80;

/**
 * The frame width mode a product is set to.
 *
 * Defaults to `standard`, including for every product written before the
 * setting existed. The exception is a product carrying the older
 * `galleryPosition` field, which was this same axis under a previous name and
 * is read for BOTH of its values: 'left' is what `standard` is now called, and
 * 'top' is what `full_width` is now called. A seller who deliberately chose
 * either keeps what they chose, because flipping a page someone configured on
 * purpose is worse than an inconsistent default.
 *
 * That second mapping used to be implicit, since 'top' agreed with the default
 * of the day and fell through to it. It is explicit now precisely because the
 * default moved: left as a fall-through it would have quietly turned every
 * old full-width page into a two-column one.
 *
 * Nothing else needs a migration script — an absent field simply reads as the
 * default, and both editors write the field on every save.
 *
 * `none` is only ever reached by an explicit stored value. Nothing infers it,
 * and it is not a fallback for anything, because a product that failed to write
 * its setting should show its images rather than silently lose them.
 */
export function resolveFrameWidthMode(
  details: { frameWidthMode?: unknown; galleryPosition?: unknown } | null | undefined,
): FrameWidthMode {
  const stored = details?.frameWidthMode;
  if (stored === 'standard' || stored === 'full_width' || stored === 'none') return stored;
  // Legacy: `galleryPosition: 'left' | 'top'` was the same axis under an older
  // name. 'left' is what 'standard' is now called, 'top' what 'full_width' is.
  if (details?.galleryPosition === 'left') return 'standard';
  if (details?.galleryPosition === 'top') return 'full_width';
  return DEFAULT_FRAME_WIDTH_MODE;
}

/**
 * Whether the gallery frame is the wide, seller-shaped one.
 *
 * Strictly `full_width`, so it governs the FRAME: its ratio, the height cap,
 * the thumbnail strip, the `sizes` attribute and the crop shape offered for
 * gallery images. It is deliberately NOT the question "does this page use the
 * stacked layout", which `none` also answers yes to. Ask that with
 * `isStackedLayout`. Collapsing the two is the bug this pair exists to prevent:
 * a `none` product read through a single boolean lands in the two-column shape
 * with the name and prose in the right-hand column and nothing in the left.
 */
export function isFullWidthGallery(
  details: { frameWidthMode?: unknown; galleryPosition?: unknown } | null | undefined,
): boolean {
  return resolveFrameWidthMode(details) === 'full_width';
}

/**
 * Whether this product shows no gallery at all.
 *
 * NOT what the render sites branch on, despite what this comment used to say.
 * They ask `isFullWidthGallery` and `isStackedLayout`, and those two compose to
 * the same answer: `none` is false for the first and true for the second, so
 * neither the full-width frame nor the left-column frame renders while
 * everything else keeps the stacked position. This is kept for callers that
 * want to ask the question directly, and for the tests that pin the mode.
 *
 * Skipping the gallery also makes the `details.coverInGallery` toggle
 * moot, since there is no gallery for the cover to be first in, which is why
 * the media step hides that switch rather than writing over its stored value:
 * a seller who switches back gets the setting they chose, not a flipped one.
 */
export function isGalleryHidden(
  details: { frameWidthMode?: unknown; galleryPosition?: unknown } | null | undefined,
): boolean {
  return resolveFrameWidthMode(details) === 'none';
}

/**
 * Whether the name, seller and short description sit in the LEFT column under
 * the gallery slot, rather than heading the right-hand column beside a square
 * frame.
 *
 * True for both `full_width` and `none`: the whole point of `none` is the
 * full-width page shape minus the gallery, so everything except the frame
 * itself stays exactly where a full-width page puts it.
 */
export function isStackedLayout(
  details: { frameWidthMode?: unknown; galleryPosition?: unknown } | null | undefined,
): boolean {
  return resolveFrameWidthMode(details) !== 'standard';
}

/**
 * The ratio a full-width gallery uses, validated against the preset list.
 *
 * Anything unrecognised falls back to the default rather than reaching a style
 * attribute: `aspectRatio` takes an arbitrary string, so an unvalidated value
 * off a stored document is a small style-injection surface and a large
 * broken-layout one.
 */
export function resolveGalleryRatio(
  details: { galleryRatio?: unknown } | null | undefined,
): string {
  const stored = details?.galleryRatio;
  return GALLERY_RATIOS.some((r) => r.value === stored)
    ? (stored as string)
    : DEFAULT_GALLERY_RATIO;
}

/**
 * `'16/9'` -> 1.777…
 *
 * Exported because the crop dialog needs the same number: its crop box is set
 * to the shape of the frame the image will end up in, and a second conversion
 * there is a second chance for the two to disagree.
 */
export function galleryRatioToNumber(ratio: string): number {
  const [w, h] = ratio.split('/').map(Number);
  return Number.isFinite(w) && Number.isFinite(h) && h > 0 ? w / h : 16 / 9;
}

/**
 * Inline style for a full-width gallery frame.
 *
 * `aspectRatio` alone is not enough. A frame at `width: 100%` with an aspect
 * ratio grows as tall as the ratio demands, so the cap has to be expressed as a
 * MAX-WIDTH derived from the height limit — capping the height directly would
 * just squash the box and break the ratio the seller picked. Paired with
 * `margin-inline: auto` on the element, a frame narrower than its container
 * centres itself, which is where any side space comes from.
 */
export function galleryFrameStyle(ratio: string): CSSProperties {
  // The page-width shape opts out of the cap entirely: no max-width means the
  // frame is exactly 100% of the content area on every viewport, which is the
  // whole point of offering it.
  if (GALLERY_RATIOS.find((r) => r.value === ratio)?.fillsWidth) {
    return { aspectRatio: ratio };
  }
  return {
    aspectRatio: ratio,
    maxWidth: `calc(${GALLERY_MAX_VH}vh * ${galleryRatioToNumber(ratio).toFixed(4)})`,
  };
}

/**
 * Width of the thumbnail rail, and the gap between it and the frame, in rem.
 *
 * MIRRORED BY THE TAILWIND CLASSES in src/components/product/gallery-thumbnails.tsx
 * (`md:w-[5.5rem]`) and by the `md:gap-3` on the block at each render site. They
 * have to agree: these numbers are what `galleryBlockStyle` below adds to the
 * frame's own ceiling, so a rail wider than this one says would push the frame
 * past the height cap instead of sitting in the space beside it.
 */
export const GALLERY_SIDE_RAIL_REM = 5.5;
export const GALLERY_SIDE_RAIL_GAP_REM = 0.75;

/**
 * Whether a gallery gets a thumbnail picker at all.
 *
 * One item needs no picker: the strip would be a single thumbnail of the
 * picture already filling the frame. The render sites need the same answer the
 * component reaches, because the rail occupies layout space around the frame
 * and a rail with nothing in it is a gap beside the picture.
 */
export function galleryShowsThumbnails(mediaCount: number): boolean {
  return mediaCount >= 2;
}

/**
 * Fewest gallery items before the STANDARD two-column frame gets a strip.
 *
 * Higher than the full-width rule above, deliberately. The standard frame is a
 * square sharing a row with the buy box, so its strip is pure added height on
 * the page carrying the price, and it already has arrows and a counter of its
 * own. At two items the arrows say everything a two-thumbnail strip would and
 * the strip is not worth the pixels; past two, "1 / 7" is a number rather than
 * a picture and the strip is what tells a buyer what they have not seen yet.
 */
export const STANDARD_GALLERY_THUMBNAIL_MIN = 3;

/**
 * Whether the STANDARD two-column gallery gets a thumbnail strip beneath it.
 *
 * The standard frame had no picker at all, on the reasoning that a strip under
 * a square frame pushes the price down for no gain. That holds for a two-item
 * gallery and stops holding after it: a seller who uploaded seven images had a
 * page where six of them were reachable only by pressing an arrow that is
 * itself invisible until the frame is hovered.
 *
 * Asked by the product page and by BOTH editor previews, for the same reason
 * every other question in this file is: a preview that disagrees with the
 * published page about whether the strip is there is the failure this file
 * exists to prevent.
 */
export function galleryShowsStandardThumbnails(mediaCount: number): boolean {
  return mediaCount >= STANDARD_GALLERY_THUMBNAIL_MIN;
}

/**
 * Whether the thumbnails sit in a RAIL BESIDE the frame rather than in a strip
 * beneath it. Full-width mode only; the standard two-column gallery puts its
 * strip underneath (see galleryShowsStandardThumbnails), never beside, because
 * a frame exactly one grid column wide has no spare margin to put a rail in.
 *
 * The split is the page-width shape against every other one, and it follows
 * from where the spare room is. Every ratio except 21:9 is subject to the
 * height cap, so on an ordinary screen the frame is NARROWER than the content
 * area and the difference is empty margin either side of it (see
 * galleryFrameStyle). Putting the picker in that margin costs the page no
 * vertical space at all, which on the page carrying the price and the buy
 * button is the space worth saving. The page-width shape has no such margin, by
 * definition: it fills the content area edge to edge, so its picker goes back
 * underneath where there is room for it.
 *
 * An unrecognised ratio answers true, matching galleryFrameStyle, which caps
 * anything it does not recognise.
 */
export function galleryThumbnailsOnSide(ratio: string, mediaCount: number): boolean {
  return galleryShowsThumbnails(mediaCount)
    && !GALLERY_RATIOS.find((r) => r.value === ratio)?.fillsWidth;
}

/**
 * Inline style for the BLOCK that holds a full-width frame and its thumbnails.
 *
 * The ceiling here is the frame's own (galleryFrameStyle) plus the rail and its
 * gap, so the frame keeps exactly the width it had before the rail existed and
 * the rail takes room that was empty margin. Both are applied: this one bounds
 * the block, the frame keeps its own, so on a narrow viewport where the rail
 * drops back underneath the frame is still capped rather than inheriting the
 * wider ceiling and overshooting the 80vh limit.
 *
 * No ceiling at all when the thumbnails are not on the side, which is both the
 * page-width shape and any gallery too short to have a picker. That is the
 * behaviour every full-width gallery had before the rail, unchanged.
 */
export function galleryBlockStyle(ratio: string, mediaCount: number): CSSProperties {
  const frameMaxWidth = galleryFrameStyle(ratio).maxWidth;
  if (!frameMaxWidth || !galleryThumbnailsOnSide(ratio, mediaCount)) {
    return { maxWidth: frameMaxWidth };
  }
  return {
    maxWidth: `calc(${frameMaxWidth} + ${GALLERY_SIDE_RAIL_REM + GALLERY_SIDE_RAIL_GAP_REM}rem)`,
  };
}

/**
 * Whether the gallery should render at all for this product.
 *
 * A soft-deleted product whose files have been swept has media URLs pointing at
 * objects onProductUpdated already deleted, so the frame shows an explanation
 * instead. The product page already made this call inline; it lives here so the
 * two preview mocks cannot answer it differently.
 */
export function galleryFilesCleared(
  product: { status?: string; filesClearedAt?: unknown } | null | undefined,
): boolean {
  return product?.status === 'deleted' && !!product?.filesClearedAt;
}

/**
 * Whether the cover image is the first item in the gallery.
 *
 * Defaults to TRUE, which is what both editors default their switch to and
 * what every product written before the setting existed should read as: the
 * cover was always the first thing in the frame, so an absent field has to
 * keep meaning that rather than silently dropping the image a seller chose as
 * the face of the product.
 */
export function resolveCoverInGallery(
  details: { coverInGallery?: unknown } | null | undefined,
): boolean {
  return details?.coverInGallery !== false;
}

/**
 * The ordered media the gallery actually shows.
 *
 * WHY THIS IS A LIB, and not three copies. `details.coverInGallery` was
 * written by both editors, honoured by both of their hand-rolled previews, and
 * never read by the real product page, which composed the list as
 * "gallery images, or the cover if there are none". That got BOTH directions
 * wrong: with the switch on (the default) a seller who uploaded any gallery
 * image lost their cover from the published frame while still seeing it in the
 * preview, and with the switch off a seller with no gallery images still got
 * the cover they had just excluded. The setting was inert on the only page
 * that matters. Putting the composition beside the rest of the gallery
 * resolvers is what stops a fourth render site answering it a fourth way.
 *
 * The cover leads when it is included, because it is the image the seller
 * picked as the face of the product and the one the marketplace card and the
 * og:image already use.
 *
 * A product with no gallery images ALWAYS falls back to the cover regardless
 * of the switch. The cover is mandatory and the switch means "is the cover
 * also part of the gallery", not "may this product have an empty frame"; the
 * way to get a page with no image on it is frame width mode `none`, which is
 * why that mode exists.
 */
export function composeGalleryMedia<T>(
  cover: T | null | undefined,
  galleryMedia: readonly T[],
  coverInGallery: boolean,
): T[] {
  if (galleryMedia.length > 0) {
    return coverInGallery && cover ? [cover, ...galleryMedia] : [...galleryMedia];
  }
  return cover ? [cover] : [];
}
